import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Reveal } from "./Reveal";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

function CountUp({ value, run }) {
  const num = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  const isNumeric = !Number.isNaN(num);
  const [display, setDisplay] = useState(isNumeric ? 0 : value);

  useEffect(() => {
    if (!isNumeric || !run) return;
    const duration = 1400;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * num));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(num);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, isNumeric, num]);

  return <span>{display}</span>;
}

export function StatsBoard() {
  const { t, tl } = useLang();
  const [active, setActive] = useState({});
  const { data: stats = [] } = useQuery({
    queryKey: ["stats-board"],
    queryFn: async () => (await api.get("/stats-board")).data,
  });

  if (stats.length === 0) return null;

  return (
    <section data-testid="stats-board" className="relative z-10 py-20 sm:py-24 bg-[#0F172A] text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="stats.eyebrow" as="span" /></span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl tracking-tight leading-tight max-w-2xl">
            <EditableText k="stats.title" as="span" />
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              onViewportEnter={() => setActive((a) => ({ ...a, [s.id]: true }))}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`stat-item-${i}`}
            >
              <div className="font-serif text-5xl sm:text-6xl tracking-tight text-[#BE923C]">
                <CountUp value={s.value} run={!!active[s.id]} />{s.suffix}
              </div>
              <div className="mt-3 h-px w-10 bg-white/20" />
              <p className="mt-3 text-sm text-white/70 leading-relaxed">{tl(s, "label")}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
